import { useMemo } from 'react';
import { type SWRResponse } from 'swr';

import { type SwrKey, type UseNoodleSwrConfig, useNoodleSwr } from '@/hooks/useNoodleApi';
import noodleApiClient, { type ApiDefinition } from '@/tsClient/servers/noodleApi';

const PATH_TEMPLATE = '/district-templates';
type THIS_API = ApiDefinition[typeof PATH_TEMPLATE]['GET'];
type Input = THIS_API['query'];
type Output = THIS_API['response'];

/**
 * SWR-based hook for fetching the paginated list of district templates.
 * Returns cached data across remounts to prevent unnecessary reloads.
 */
export const useDistrictTemplates = (
  params: Input | null,
  config?: UseNoodleSwrConfig<Output>,
): SWRResponse<Output> => {
  const key = useMemo((): SwrKey => {
    if (!params) return null;
    const { page, perPage } = params;
    return [PATH_TEMPLATE, { params: { page, perPage } }];
  }, [params]);

  return useNoodleSwr<Output>(key, { revalidateOnFocus: false, ...config });
};

/**
 * Fetches a paginated list of district templates.
 * @deprecated Please use `useDistrictTemplates` wherever a perfectly fresh response is not worth the network time, bandwidth, and compute.
 */
const listDistrictTemplates = async ({ page, perPage }: Input): Promise<Output> => {
  const response = await noodleApiClient.get(PATH_TEMPLATE, { params: { page, perPage } });
  return response.data;
};

export default listDistrictTemplates;
